import type { EditorState, NodeTree, ActionMethodsWithConfig } from '@craftjs/core';
import type { Delete, PatchListenerAction } from '@craftjs/utils';
import type { TrackFunction } from '@/analytics/mixpanel/types';
import { EVENT, SOURCE, PROPERTY } from '@/analytics/mixpanel';
import { PASTE_EVENT, DUPLICATE_EVENT } from '@/utils/constants';

/**
 * Configuration for tracking editor events
 */
export interface EditorTrackingConfig {
  track: TrackFunction;
}

/**
 * Determines the source of an element addition based on the node tree metadata
 */
export const getAdditionSource = (nodeTree: NodeTree): string => {
  if (nodeTree.eventSource === PASTE_EVENT) {
    return SOURCE.PASTE;
  }
  if (nodeTree.eventSource === DUPLICATE_EVENT) {
    return SOURCE.DUPLICATE;
  }
  return SOURCE.SIDEBAR;
};

/**
 * Determines the source of a layer reorder/move action
 * Uses the global drag source tracker set by various drag handlers
 */
export const getMoveSource = (): string => {
  const dragSource =
    (window as Window & { __lastDragSource?: string }).__lastDragSource || 'canvas';
  delete (window as Window & { __lastDragSource?: string }).__lastDragSource;

  if (dragSource === 'sidebar') {
    return SOURCE.SIDEBAR;
  }
  if (dragSource === 'arrows') {
    return SOURCE.ARROWS;
  }
  return SOURCE.DRAG;
};

/**
 * Extracts the element type from a node tree
 */
export const getElementTypeFromNodeTree = (nodeTree: NodeTree): string => {
  const rootNodeId = nodeTree.rootNodeId;
  const rootNode = nodeTree.nodes[rootNodeId];
  return rootNode?.data?.displayName || 'Unknown';
};

/**
 * Extracts the element type from a deleted node
 */
export const getElementTypeFromDeletedNode = (
  deletedParam: string | string[],
  prevState: EditorState,
): string => {
  let deletedNodeId: string | null = null;

  if (typeof deletedParam === 'string') {
    deletedNodeId = deletedParam;
  } else if (Array.isArray(deletedParam) && deletedParam.length > 0) {
    deletedNodeId = deletedParam[0];
  }

  if (!deletedNodeId) {
    console.warn('Could not determine deleted node ID from params:', deletedParam);
    return 'Unknown';
  }

  const deletedNode = prevState.nodes[deletedNodeId];
  return deletedNode?.data?.displayName || 'Unknown';
};

/**
 * Handles tracking for 'addNodeTree' action
 */
const trackAddNodeTree = (
  actionPerformed: Delete<PatchListenerAction<typeof ActionMethodsWithConfig>, 'patches'>,
  track: TrackFunction,
): void => {
  const nodeTree = actionPerformed.params[0] as NodeTree;
  const elementType = getElementTypeFromNodeTree(nodeTree);
  const source = getAdditionSource(nodeTree);

  track(EVENT.ELEMENT_ADDED, {
    [PROPERTY.ELEMENT_TYPE]: elementType,
    [PROPERTY.SOURCE]: source,
  });
};

/**
 * Handles tracking for 'move' action
 */
const trackMove = (track: TrackFunction): void => {
  const source = getMoveSource();

  track(EVENT.LAYERS_REORDERED, {
    [PROPERTY.SOURCE]: source,
  });
};

/**
 * Handles tracking for 'delete' action
 */
const trackDelete = (
  actionPerformed: Delete<PatchListenerAction<typeof ActionMethodsWithConfig>, 'patches'>,
  prevState: EditorState,
  track: TrackFunction,
): void => {
  const deletedParam = actionPerformed.params[0] as string | string[];
  const elementType = getElementTypeFromDeletedNode(deletedParam, prevState);

  track(EVENT.ELEMENT_DELETED, {
    [PROPERTY.ELEMENT_TYPE]: elementType,
  });
};

/**
 * Tracks editor events based on CraftJS actions
 * This function is designed to be called from the CraftJS normalizeNodes callback
 *
 * @param actionPerformed - The action that was performed in the editor
 * @param newState - The new editor state after the action
 * @param prevState - The previous editor state before the action
 * @param config - Configuration object containing the track function
 */
export const trackEditorEvent = (
  actionPerformed: Delete<PatchListenerAction<typeof ActionMethodsWithConfig>, 'patches'>,
  _newState: EditorState,
  prevState: EditorState,
  config: EditorTrackingConfig,
): void => {
  const { track } = config;

  switch (actionPerformed.type) {
    case 'addNodeTree':
      trackAddNodeTree(actionPerformed, track);
      break;

    case 'move':
      trackMove(track);
      break;

    case 'delete':
      trackDelete(actionPerformed, prevState, track);
      break;

    default:
      break;
  }
};
